import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { generatedApiClient } from '@/services/generatedApiClient';
import { cacheConfig } from '@/services/queryClient';
import type {
  ApiLoginInput,
  ApiRegisterInput,
  ApiVerifyOTPInput,
  ApiChangePasswordInput,
} from '../../generated/api';

// Query keys for consistent cache management
export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
  usage: () => [...authKeys.all, 'usage'] as const,
} as const;

/**
 * Hook for user login
 */
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: ApiLoginInput) => {
      const response = await generatedApiClient.login(credentials);
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch user data after successful login
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
};

/**
 * Hook for user registration
 */
export const useRegister = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: ApiRegisterInput) => {
      const response = await generatedApiClient.register(userData);
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      // Invalidate and refetch user data after successful registration
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
    onError: (error) => {
      console.error('Registration failed:', error);
    },
  });
};

/**
 * Hook for user logout
 */
export const useLogout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await generatedApiClient.logout();
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      // Clear all auth-related cache after logout
      queryClient.removeQueries({ queryKey: authKeys.all });
      queryClient.clear(); // Clear all cache to ensure clean state
    },
    onError: (error) => {
      console.error('Logout failed:', error);
      // Even if logout fails, clear local cache
      queryClient.removeQueries({ queryKey: authKeys.all });
    },
  });
};

/**
 * Hook for getting current user data with optimized caching
 */
export const useMe = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: authKeys.me(),
    queryFn: async () => {
      const response = await generatedApiClient.getMe();
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    },
    // Use optimized cache settings for user data
    staleTime: cacheConfig.user.staleTime,
    gcTime: cacheConfig.user.gcTime,
    retry: (failureCount, error) => {
      // Don't retry on 401 (unauthorized) errors
      if (error instanceof Error && error.message.includes('401')) {
        return false;
      }
      return failureCount < 2;
    },
    // Enable background refetching for better UX
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Hook for requesting OTP
 */
export const useRequestOTP = () => {
  return useMutation({
    mutationFn: async (input?: {
      email?: string;
      username?: string;
      action?: string;
    }) => {
      const response = await generatedApiClient.requestOTP(input);
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onError: (error) => {
      console.error('OTP request failed:', error);
    },
  });
};

/**
 * Hook for verifying OTP
 */
export const useVerifyOTP = () => {
  return useMutation({
    mutationFn: async (otpData: ApiVerifyOTPInput) => {
      const response = await generatedApiClient.verifyOTP(otpData);
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onError: (error) => {
      console.error('OTP verification failed:', error);
    },
  });
};

/**
 * Hook for changing password
 */
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (data: ApiChangePasswordInput) => {
      const response = await generatedApiClient.changePassword(data);
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onError: (error) => {
      console.error('Password change failed:', error);
    },
  });
};

/**
 * Hook for regenerating Bearer token
 */
export const useRegenerateToken = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await generatedApiClient.regenerateToken();
      if (response.error) {
        throw new Error(response.error);
      }
      return response;
    },
    onSuccess: () => {
      // Refetch user data to get the new token
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
    },
    onError: (error) => {
      console.error('Token regeneration failed:', error);
    },
  });
};

/**
 * Hook to check if user is authenticated
 */
export const useIsAuthenticated = () => {
  const { data: user, isLoading, error } = useMe();
  
  return {
    isAuthenticated: !!user && !error,
    isLoading,
    user,
  };
};
